import React, {
  useContext, useEffect, useState, useMemo
} from 'react'
import { Button, Modal } from '@telus/koodo-ui-kit'
import { useSelector } from 'react-redux'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import Sites from '@sites/index'
import { getById, getBySlug } from '@apps/utils/contentful'
import { containsProvince } from '@apps/utils/common'
import { PHONE_NUMBER_STATE } from '@apps/redux/features/PhoneNumberSlice'
import { PHONE_NUMBER_OPTIONS } from '@apps/utils/constants'

const PlanUnavailablePopup = ({
  province,
  onClose,
  onNewPhoneNumberClick,
  onNewPlanClick,
  onValidate,
  disableClose
}) => {
  const {
    [PLANS_STATE.PLAN]: planInfo,
    localizedPlanItem
  } = useSelector((state) => state.plansPage)

  const { [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER_OPTION]: selectedPhoneNumberOption } =
    useSelector((state) => state.phoneNumberPage)
  const { userType } = useSelector((state) => state.common)
  const { pageContent } = useSelector((state) => state.common.content)

  const isDealer = useMemo(() => {
    return userType === Sites.dealer
  }, [userType])

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.PROVINCES]: mappedProvinceList
  } = useContext(ApplicationContext)

  const selectedNewNumber = selectedPhoneNumberOption === PHONE_NUMBER_OPTIONS.CHOOSE_A_NEW_NUMBER

  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'
  const phoneNumberPageContent = getById(pageContent, `phoneNumberPage${pagePrefix}`) || {}
  const { shortTextFields, longTextFields } = phoneNumberPageContent[language] || {}

  const textContent = useMemo(() => {
    return {
      incompatibleNumberMessage: getBySlug(shortTextFields, 'incompatibleNumberMessage')?.value,
      incompatiblePortInMessage: getBySlug(longTextFields, 'incompatiblePortInMessage')?.value,
      incompatibleNumberTitle: getBySlug(shortTextFields, 'incompatibleNumberTitle')?.value,
      incompatiblePortInTitle: getBySlug(shortTextFields, 'incompatiblePortInTitle')?.value,
      selectNewPhoneNumber: getBySlug(shortTextFields, 'selectNewPhoneNumber')?.value,
      selectNewPlan: getBySlug(shortTextFields, 'selectNewPlan')?.value
    }
  }, [shortTextFields])

  const { selectNewPhoneNumber, selectNewPlan } = textContent

  const popupTextContent = useMemo(() => {
    if (selectedNewNumber) {
      return {
        title: textContent?.incompatibleNumberTitle,
        message: textContent?.incompatibleNumberMessage
      }
    }
    return {
      title: textContent?.incompatiblePortInTitle,
      message: textContent?.incompatiblePortInMessage
    }
  }, [textContent, selectedNewNumber])

  const [showPlanUnavailable, setShowPlanUnavailable] = useState(false)

  const normalizeString = (value) => {
    return value == null ? '' : String(value).trim().toLowerCase()
  }

  const toNumberOrNull = (value) => {
    const num = Number(value)
    return Number.isNaN(num) ? null : num
  }

  const normalizeProvince = (prov) => {
    if (prov == null || prov === '') return ''

    const raw = String(prov).trim()
    if (raw.length === 2) return raw

    const match = mappedProvinceList?.find((item) => {
      const shortName = item?.shortName ? String(item.shortName).toLowerCase() : ''
      const value = item?.value != null ? String(item.value).toLowerCase() : ''
      const en = item?.label?.en ? String(item.label.en).toLowerCase() : ''
      const fr = item?.label?.fr ? String(item.label.fr).toLowerCase() : ''
      const lookup = raw.toLowerCase()
      return shortName === lookup || value === lookup || en === lookup || fr === lookup
    })

    return match?.shortName || raw
  }

  const validateCurrentPlanInProvince = async (prov) => {
    const normalizedProvince = normalizeProvince(prov)
    if (!normalizedProvince) {
      return
    }

    const langPlans = localizedPlanItem?.[language]
    // Do not evaluate incompatibility until localized catalog is available.
    if (!Array.isArray(langPlans) || langPlans.length === 0) {
      return
    }

    let planAvailable = false
    const selectedPlanId = normalizeString(
      planInfo?.id
      ?? planInfo?.pricePlanId
      ?? planInfo?.commerce?.id
      ?? planInfo?.commerce?.planId
      ?? planInfo?.commerce?.pricePlanId
    )
    const selectedPlanGuid = normalizeString(
      planInfo?.guid
      ?? planInfo?.pricePlanGuid
      ?? planInfo?.commerce?.guid
      ?? planInfo?.commerce?.pricePlanGuid
    )
    const selectedPlanSlug = normalizeString(
      planInfo?.sluggedName
      ?? planInfo?.commerce?.sluggedName
    )
    const selectedPlanValue = toNumberOrNull(planInfo?.value)
    const selectedPlanDuration = toNumberOrNull(planInfo?.duration)

    const { plans } = langPlans[0] || {}
    let hasIdentityMatch = false
    planAvailable = plans?.some((plan) => {
      const planId = normalizeString(plan?.id)
      const planGuid = normalizeString(plan?.guid)
      const planSlug = normalizeString(plan?.sluggedName)
      const planValue = toNumberOrNull(plan?.value)
      const planDuration = toNumberOrNull(plan?.duration)

      // MFE may provide a different id type/shape than the localized catalog.
      // Prefer guid, then id, then slug, then value+duration fallback.
      const isSamePlan =
        (selectedPlanGuid && planGuid && selectedPlanGuid === planGuid)
        || (selectedPlanId && planId && selectedPlanId === planId)
        || (selectedPlanSlug && planSlug && selectedPlanSlug === planSlug)
        || (
          selectedPlanValue !== null
          && planValue !== null
          && selectedPlanValue === planValue
          && selectedPlanDuration !== null
          && planDuration !== null
          && selectedPlanDuration === planDuration
        )

      if (!isSamePlan) {
        return false
      }

      hasIdentityMatch = true
      return (
        !plan?.province
        || (Array.isArray(plan.province) && plan.province.length === 0)
        || containsProvince(plan?.province, normalizedProvince)
      )
    })

    // If the selected plan cannot be matched in localized catalog at all,
    // skip incompatibility popup to avoid false positives for MFE IDs.
    if (!hasIdentityMatch) {
      setShowPlanUnavailable(false)
      if (onValidate) {
        onValidate(true)
      }
      return
    }

    if (!planAvailable) {
      setShowPlanUnavailable(true)
    } else {
      setShowPlanUnavailable(false)
    }
    if (onValidate) {
      onValidate(planAvailable)
    }
  }

  useEffect(() => {
    if (province && (planInfo?.id || planInfo?.guid)) {
      validateCurrentPlanInProvince(province)
    }
  }, [province, planInfo?.id, planInfo?.guid, localizedPlanItem, language])

  const handleClosePopup = () => {
    setShowPlanUnavailable(false)
  }

  const handleClose = () => {
    handleClosePopup()
    if (onClose) {
      onClose()
    }
  }

  const handlePhoneNumberClick = () => {
    handleClosePopup()
    if (onNewPhoneNumberClick) {
      onNewPhoneNumberClick()
    }
  }

  const handleChangePlanClick = () => {
    handleClosePopup()
    if (onNewPlanClick) {
      onNewPlanClick()
    }
  }

  return (
    <Modal
      isOpen={showPlanUnavailable}
      onClose={handleClose}
      heading={popupTextContent?.title}
      bodyText={popupTextContent?.message}
      showControls={!disableClose}
      disableClose={disableClose}
      tokens={{
        maxWidth: 750,
        height: 'auto',
        marginRight: 0,
        headingPaddingRight: 0
      }}
    >
      <div className="text-black leading-6 text-sm">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-8 mt-6">
          {selectedNewNumber && (
            <div className="sm:mr-3">
              <Button
                onPress={handlePhoneNumberClick}
                tokens={{ marginRight: 12 }}
                testId="planUnavailable-selectNewPhoneNumber"
              >
                {selectNewPhoneNumber}
              </Button>
            </div>
          )}

          <Button
            onPress={handleChangePlanClick}
            testId="planUnavailable-selectNewPlan"
            variant={{ text: selectedNewNumber, priority: 'high' }}>
            {selectNewPlan}
          </Button>

        </div>
      </div>
    </Modal>
  )
}

export default PlanUnavailablePopup
